<template>
	<div class="p-component p-fluid p-formgrid p-grid">
		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ trainingKey.description }}</p>
		</div>
		<div  class="p-field p-input-filled p-col-9">
			<InputText type="text" v-model="trainingKey.value.value" disabled />
			<small v-if="!(trainingKey.value.value !== '' && trainingKey.value.value !== null)" class="p-error">{{$t("Squeeze.Training.TrainingKey")}}</small>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t("Squeeze.Training.Indicator") }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="indicatorId"
				:options="allIndicators"
				optionLabel="description"
				optionValue="id"
				@change="getAllRegionAnchors"
			/>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t('Squeeze.Training.Region') + ' ' + $t('Squeeze.Training.Anchor') }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="regionAnchorId"
				:options="allRegionAnchors"
				optionLabel="description"
				optionValue="id"
				@change="getAllItemFields"
			/>
		</div>

		<div class="p-col-3">
			<p style="font-size: 1rem;">{{ $t('Squeeze.Training.Region') + ' ' + $t('Squeeze.Training.Fields') }}</p>
		</div>
		<div class="p-field p-input-filled p-col-9">
			<Dropdown
				v-model="itemFieldId"
				:options="allItemFields"
				optionLabel="valuePattern"
				style="height: 2.84rem;"
			>
				<template #value="slotProps">
					{{ getItemFieldValueDescription(slotProps.value) }}
				</template>
				<template #option="slotProps">
					{{ getItemFieldDescription(slotProps.option) }}
				</template>
			</Dropdown>
		</div>

		<div class="p-pr-0 p-col-12 p-formgrid p-grid">
			<div class="p-col-3">
				<Button
					:label="$t('Squeeze.Training.Test')"
					:disabled="!indicatorId && trainingKey.value.value !== '' && trainingKey.value.value !== null"
					@click="getTestData"
				/>
			</div>
			<div class="p-col-6 p-offset-3 p-pr-0">
				<Button
					:label="$t('Squeeze.Training.ResetTestFilter')"
					:disabled="!indicatorId && !regionAnchorId && !itemFieldId"
					@click="resetTestFilter"
				/>
			</div>
		</div>
	</div><br/>

	<DataTable
		class="p-component p-shadow-2"
		:value="testData"
		:autoLayout="true"
		:rowHover="true"
		:loading="loading"
		@row-click="onMarkRegion"
		:scrollable="true"
		scrollHeight="flex"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.Reports.Data') }) }}
		</template>
		<Column field="value" :header="$t('Squeeze.Training.ResultValue')"/>
		<Column field="boundingBox.page" :header="$t('Squeeze.Locators.Site')"></Column>
		<Column field="boundingBox.x0" header="x0"></Column>
		<Column field="boundingBox.y0" header="y0"></Column>
		<Column field="boundingBox.x1" header="x1"></Column>
		<Column field="boundingBox.y1" header="y1"></Column>
	</DataTable>
</template>

<script lang="ts">
import {defineComponent, onBeforeMount, onMounted, PropType, reactive, ref} from "vue";
import {useI18n} from "vue-i18n";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {
	DocumentField,
	DocumentFieldValue,
	DocumentTable,
	DocumentTableColumn,
	FormTrainingItemField,
	FormTrainingItemRegionAnchor,
	FormTrainingRegionAnchor
} from "@dex/squeeze-client-ts";
import {ToastManager} from "@/util/ToastManager";
import {useToast} from "primevue/usetoast";
import {ClientManager} from "@/singletons/ClientManager";
import {getTranslatedDescription} from "@/util/TranslationHelper";

export default defineComponent({
	name: "FormTrainingTestTable",
	components: {
		InputText,
		Dropdown,
		Button,
		DataTable,
		Column,
	},
	props: {
		trainingKeyField: {
			type: Object as PropType<DocumentField>,
			default: () => ({}),
		},
		documentClassId: {
			type: Number,
			required: true,
		},
		documentId: {
			type: Number,
			required: true,
		},
		testFilter: {
			type: Object as PropType<any>,
			default: () => ({}),
		},
	},
	emits: [
		'onMarkRegion',
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();

		/** Current training key field */
		const trainingKey = reactive<DocumentField>({});

		/** Show Loading on load data */
		const loading = ref<boolean>(false);

		/** Selected Indicator ID */
		const indicatorId = ref<number>(0);

		/** Selected region anchor ID */
		const regionAnchorId = ref<number>(0);

		/** Selected item field ID */
		const itemFieldId = ref<any>(0);

		/** All indicators */
		const allIndicators = ref<FormTrainingItemRegionAnchor[]>([]);

		/** All region anchors */
		const allRegionAnchors = ref<FormTrainingRegionAnchor[]>([]);

		/** All item fields */
		const allItemFields = ref<FormTrainingItemField[]>([]);

		/** Current table columns used by the indicator */
		const currentTableColumns = ref<DocumentTableColumn[]>([]);

		/** Test data */
		const testData = ref<DocumentFieldValue[]>([]);

		/** Document API endpoint */
		const documentApi = ClientManager.getInstance().squeeze.document;

		/** Document Class API endpoint */
		const documentClassApi = ClientManager.getInstance().squeeze.documentClass;

		/** Form Training API endpoint */
		const formTrainingApi = ClientManager.getInstance().squeeze.formTraining;

		/** Get test data of line items*/
		const getTestData = () => {
			loading.value = true;
			if (!indicatorId.value) {
				return;
			}
			documentApi.testLineFormTraining(props.documentId, trainingKey.value!.value!, indicatorId.value, regionAnchorId.value, itemFieldId.value ? itemFieldId.value.id : itemFieldId.value)
				.then((data: DocumentFieldValue[]) => {
					testData.value = data;
				}).catch(response => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Reset test filter */
		const resetTestFilter = () => {
			indicatorId.value = 0;
			regionAnchorId.value = 0;
			itemFieldId.value = 0;

			allRegionAnchors.value = [];
			allItemFields.value = [];
			testData.value = [];
		}

		/**
		 * Click a row to mark
		 * @param event
		 */
		const onMarkRegion = (event: {originalEvent: PointerEvent; data: any; index: number}) => {
			emit("onMarkRegion", [event.data.boundingBox]);
		}

		/** Get all Indicators */
		const getAllIndicators = () => {
			loading.value = true;
			formTrainingApi.getFormTableItemIndicator(props.documentClassId, trainingKey.value!.value!)
				.then(data => {
					allIndicators.value = data;
				}).catch(response => response.json().then ((err: { message: string; statusCode: number }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/**
		 * Get all table columns
		 * We need this for the value description for the dropdown of item fields
		 */
		const getAllTableColumns = () => {
			const currentIndicator = allIndicators.value.find(indicator => indicator.id === indicatorId.value);
			documentClassApi.getDocumentClassTableById(props.documentClassId, currentIndicator!.tableId!)
				.then((data: DocumentTable) => {
					currentTableColumns.value = data.columns || [];
				}).catch(response => response.json().then ((err: { message: string; statusCode: number }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				}))
		}

		/** Get all region anchors */
		const getAllRegionAnchors = () => {
			loading.value = true;
			if (!indicatorId.value) {
				return;
			}
			// reset after change of indicator
			if (regionAnchorId.value && props.testFilter && props.testFilter.indicatorId !== indicatorId.value) {
				regionAnchorId.value = 0;
				itemFieldId.value = 0;
				allItemFields.value = [];
			}

			formTrainingApi.getFormItemRegionAnchors(props.documentClassId, indicatorId.value)
				.then(data => {
					allRegionAnchors.value = data;
					getAllTableColumns();
				}).catch(response => response.json().then ((err: { message: string; statusCode: number }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/** Get all item fields */
		const getAllItemFields = () => {
			loading.value = true;
			// reset after change of region anchor
			if (itemFieldId.value && props.testFilter && props.testFilter.regionAnchorId !== regionAnchorId.value) {
				itemFieldId.value = 0;
			}

			formTrainingApi.getFormItemFields(props.documentClassId, regionAnchorId.value)
				.then(data => {
					allItemFields.value = data;
				}).catch(response => response.json().then ((err: { message: string; statusCode: number }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
		}

		/**
		 * Get item field value description
		 * @param slotProp
		 */
		const getItemFieldValueDescription = (slotProp: any) => {
			if (!slotProp.id) return '';
			const itemField = allItemFields.value.find((itemField: any) => itemField.id === slotProp.id);
			if (!itemField) return t('Squeeze.DocumentClasses.ItemFields') + "-ID: " + slotProp.id;

			let column = allRegionAnchors.value.find((column: any) => column.id === regionAnchorId.value);
			if (props.testFilter && props.testFilter.columns) {
				column = props.testFilter.columns.find((column: any) => column.id === itemField.columnId);
			}
			if (!column) return itemField.valuePattern + " | " + t('Squeeze.DocumentClasses.TableColumns') + "-ID: " + itemField.columnId;
			return itemField.valuePattern + " | " + getTranslatedDescription(column);
		}

		/**
		 * Get item field description
		 * @param slotProps
		 */
		const getItemFieldDescription = (slotProps: any) => {
			let column = currentTableColumns.value.find((column: any) => column.id === slotProps.columnId);
			if (props.testFilter && props.testFilter.columns) {
				column = props.testFilter.columns.find((column: any) => column.id === slotProps.columnId);
			}
			if (!column) return slotProps.valuePattern + " | " + t('Squeeze.DocumentClasses.TableColumns') + "-ID: " + slotProps.columnId;
			return slotProps.valuePattern + " | " + getTranslatedDescription(column);
		}

		onMounted(() => {
			if (!trainingKey.value!.value) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.Viewer.Error.NoCreditorField'));
				return;
			}

			getAllIndicators();

			if (props.testFilter.regionAnchorId) {
				indicatorId.value = props.testFilter.indicatorId;
				regionAnchorId.value = props.testFilter.regionAnchorId;
				itemFieldId.value = props.testFilter;
				getAllItemFields();
			} else if (props.testFilter.indicatorId) {
				indicatorId.value = props.testFilter.indicatorId;
				regionAnchorId.value = props.testFilter.id;
				getAllItemFields();
			} else {
				indicatorId.value = props.testFilter.id;
			}

			getAllRegionAnchors();
			getTestData();
		})

		onBeforeMount(() => {
			Object.assign(trainingKey, props.trainingKeyField);
		})

		return {
			t,
			toast,
			trainingKey,
			loading,
			indicatorId,
			regionAnchorId,
			itemFieldId,
			allIndicators,
			allRegionAnchors,
			allItemFields,
			testData,
			getTestData,
			resetTestFilter,
			onMarkRegion,
			getAllIndicators,
			getAllRegionAnchors,
			getAllItemFields,
			getItemFieldValueDescription,
			getItemFieldDescription,
		}
	},
});
</script>

<style scoped>

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

</style>
